import React, { useEffect, useMemo, useState } from "react";
import { ErrorMessage, Flex, QueryLoader } from "@jsq/ds/core";
import { CancelButton, SubmitButton, useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { useComplianceCaseContext } from "../../entrypoints/ComplianceCaseContext";
import { useGetAmlQuestionsByAccountTypeQuery } from "../../graphql/supergraphHooks";
import { type AccountTypeEnum } from "../../graphql/supergraphTypes";
import { clientContext } from "../../utils/graphql_utils";
import { AddressInput } from "../AddressInput";
import { EntityInformationInput } from "../EntityInformationInput";
import { EntityTypeSurvey } from "../EntityTypeSurvey";
import { Question } from "../EntityTypeSurvey/types";
import {
  provideDefaultQuestionIds,
  provideQuestionsWithAnswers,
} from "../InstitutionEditScreen/utils";
import { useProfileQuestions } from "./hooks/useProfileQuestions";
import { EntityProfile } from "./types";

export interface EntityFormContentProps {
  onBack: () => void;
  isUpdating: boolean;
  profileId?: number;
  amlEntityTypeId?: number | null;
  onDefaultQuestionsChange?: (defaultQuestions: number[]) => void;
}

export const EntityFormContent: React.FC<EntityFormContentProps> = ({
  onBack,
  isUpdating,
  profileId,
  amlEntityTypeId,
  onDefaultQuestionsChange,
}) => {
  const formContext = useFormContext<EntityProfile>();
  const entityType = formContext.values.entityType as AccountTypeEnum;
  const { caseId } = useComplianceCaseContext();

  const [currentAmlEntityTypeId, setCurrentAmlEntityTypeId] = useState<number | null>(
    amlEntityTypeId || null,
  );
  const [previousEntityType, setPreviousEntityType] = useState<string>(entityType);

  const amlQuestionsQueryResult = useGetAmlQuestionsByAccountTypeQuery({
    context: clientContext(),
    variables: { caseId: caseId, accountType: entityType },
    skip: !entityType || !caseId,
    fetchPolicy: "no-cache",
  });

  const questionResponses = useProfileQuestions(amlQuestionsQueryResult?.data, profileId);

  const defaultQuestions = useMemo(
    () => provideDefaultQuestionIds(questionResponses || []),
    [questionResponses],
  );

  const nonDefaultQuestionsWithAnswers = useMemo(
    () => provideQuestionsWithAnswers(questionResponses || [], currentAmlEntityTypeId),
    [questionResponses, currentAmlEntityTypeId],
  );

  useEffect(() => {
    if (entityType !== previousEntityType) {
      setPreviousEntityType(entityType);
      if (defaultQuestions.length > 0) {
        setCurrentAmlEntityTypeId(defaultQuestions[0]);
      } else {
        setCurrentAmlEntityTypeId(null);
      }
    }
  }, [entityType, previousEntityType, defaultQuestions]);

  useEffect(() => {
    if (onDefaultQuestionsChange) {
      onDefaultQuestionsChange(defaultQuestions);
    }
  }, [defaultQuestions, onDefaultQuestionsChange]);

  const onSurveyChange = (updatedQuestions: Question[]) => {
    void formContext.setFieldValue("updatedQuestions", updatedQuestions, true);
  };

  return (
    <>
      <EntityInformationInput title="Details" meta={{ headerType: "h4" }} />
      <>
        <AddressInput title="Domicile address" meta={{ headerType: "strong-md" }} />
        <QueryLoader result={[amlQuestionsQueryResult]}>
          {nonDefaultQuestionsWithAnswers && (
            <Flex flexDirection="column">
              <EntityTypeSurvey
                questions={nonDefaultQuestionsWithAnswers}
                onSurveyChange={onSurveyChange}
              />
              {formContext.touched.updatedQuestions && formContext.errors.updatedQuestions && (
                <ErrorMessage>{formContext.errors.updatedQuestions.toString()}</ErrorMessage>
              )}
            </Flex>
          )}
        </QueryLoader>
      </>

      <Flex justifyContent="flex-end" gap={Sizes.MD}>
        <CancelButton onClick={onBack}>Back</CancelButton>
        <SubmitButton isLoading={isUpdating}>Save and exit</SubmitButton>
      </Flex>
    </>
  );
};
